import { useQuery } from '@tanstack/vue-query'
import { type Ref, computed } from 'vue'

import { useRankingSource } from '@/composables/use-ranking-devtools'
import { DEFAULT_CATEGORY_PAGE_SIZE } from '@/constants/general'
import { useI18nAddon } from '@/i18n/i18n'
import { usePersisterFn } from '@/query/persister-plugin'
import { Persisters } from '@/query/shared'
import { useAppStore } from '@/store/app-store'
import type { CategoryType, ShortGameType, ShortGameZipType } from '@/types'
import { unzipShortGame, zipAPIGame } from '@/utils/helpers'
import { ApiRequestError } from '@/utils/requests/api-error'
import { useAPI } from '@/utils/requests/api-plugin'

import { useCategorySeoInfo } from './use-category-seo-info'

type Category = {
    id: string
    categoryType: CategoryType
    total: number
    totalPages: number
}

type UseCategoryPageDataParams = {
    categoryName: Ref<string>
    pageType: Ref<CategoryType>
    page: Ref<number>
    pinnedGame: Ref<string | undefined>
}

export const useCategoryPageData = ({ categoryName, pageType, page, pinnedGame }: UseCategoryPageDataParams) => {
    const appStore = useAppStore()
    const { currentLocale } = useI18nAddon()
    const { getGamesByCategories } = useAPI()

    const {
        categoryInfo: seoInfo,
        categoryRedirect,
        fetchStatus: seoFetchStatus,
        hasError: seoHasError,
        suspense: seoSuspense,
    } = useCategorySeoInfo({ categoryName, pageType })

    const gamesQueryKey = computed(() => [
        'category-games-data',
        categoryName.value,
        pageType.value,
        page.value,
        pinnedGame.value,
        currentLocale.value,
        appStore.platform,
        appStore.rankingParams?.clusterId,
        appStore.rankingParams?.audience,
        appStore.withDebugInfo,
        appStore.contentScope,
    ])
    const firstPagePersister = usePersisterFn(Persisters.MediumCardinality)
    const gamesQueryPersister = computed(() => {
        if (page.value === 1) {
            return firstPagePersister
        }
        return undefined
    })

    const {
        data: gamesData,
        suspense: gamesSuspense,
        isError: gamesHasError,
    } = useQuery({
        queryKey: gamesQueryKey,
        queryFn: async () => {
            const result: {
                games: ShortGameZipType[]
                category?: Category
                fetchStatus?: 404 | 410
            } = {
                games: [],
            }

            // contentScope indicates we want to show specific content to user
            // meanwhile games on unsupported platforms need to be shown only for SEO
            // so defined contentScope disables this flag
            const includeUnsupportedPlatforms = !appStore.contentScope

            if (isNaN(page.value)) {
                result.fetchStatus = 404
                return result
            }

            const response = await getGamesByCategories({
                categoriesIds: [categoryName.value],
                pagination: {
                    page: page.value,
                    pageSize: DEFAULT_CATEGORY_PAGE_SIZE,
                },
                platform: appStore.platform,
                locale: currentLocale.value,
                withDebugInfo: appStore.withDebugInfo,
                pageType: pageType.value,
                pinnedGame: pinnedGame.value,
                rankingClusterId: appStore.rankingParams?.clusterId,
                rankingAudience: appStore.rankingParams?.audience,
                includeUnsupportedPlatforms,
                scope: appStore.contentScope,
            })

            if (response.status === 'error') {
                throw new ApiRequestError(response)
            }

            if (response.status === 'not-found') {
                result.fetchStatus = response.httpStatus
                return result
            }

            const [fetchedCategory] = response.data.categories

            if (!fetchedCategory) {
                return result
            }

            if (page.value > 1 && fetchedCategory.games.length === 0) {
                result.fetchStatus = 404
                return result
            }

            result.games = fetchedCategory.games.map(zipAPIGame)
            result.category = {
                id: fetchedCategory.id,
                categoryType: fetchedCategory.categoryType,
                total: fetchedCategory.totalCount,
                totalPages: Math.ceil(fetchedCategory.totalCount / DEFAULT_CATEGORY_PAGE_SIZE),
            }

            return result
        },
        persister: gamesQueryPersister,
    })

    // Combine the results
    const games = computed<ShortGameType[]>(() => gamesData.value?.games.map(unzipShortGame) || [])

    const pageSize = computed(() => DEFAULT_CATEGORY_PAGE_SIZE)
    const category = computed(() => gamesData.value?.category)
    const categoryInfo = computed(
        () => seoInfo.value && { ...seoInfo.value, fullTitle: seoInfo.value.description || seoInfo.value.title },
    )
    const fetchStatus = computed(() => seoFetchStatus.value || gamesData.value?.fetchStatus)
    const hasError = computed(() => seoHasError.value || gamesHasError.value)

    // Combined suspense for SSR - both queries must complete
    const preloadCategoryPageData = async () => {
        await Promise.all([seoSuspense(), gamesSuspense()])
    }

    useRankingSource(() => [{ title: categoryInfo.value?.title || categoryName.value, games: games.value }])

    return {
        games,
        pageSize,
        category,
        categoryInfo,
        categoryRedirect,
        fetchStatus,
        hasError,
        preloadCategoryPageData,
    }
}
